import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';
import type { BankhubReconcileRow, DriftFlag } from '@repo/shared-types';
import { Badge, type BadgeProps } from '@repo/ui/src/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import { DataTable, type DataTableColumnMeta } from '../components/DataTable';
import { BankhubRowActions } from './components/BankhubRowActions';
import { useReconcile } from '../hooks/useBankhub';

const ALL_FLAGS = '__all__';

const FLAG_LABEL: Record<DriftFlag, string> = {
  OK: 'OK',
  ORPHAN: 'Orphan',
  GHOST_BANK: 'Ghost bank',
  STATUS_MISMATCH: 'Lệch status',
  DANGLING: 'Dangling',
  NO_BANK: 'Chưa có bank',
};

// OK is calm (soft green); ORPHAN/GHOST_BANK/DANGLING mean the platform is
// billing against something that no longer lines up on one side and need
// action soon (solid destructive); STATUS_MISMATCH/NO_BANK are a heads-up,
// not yet broken (soft amber) — same tint vocabulary as UsersPage's verified
// badge.
const FLAG_VARIANT: Record<DriftFlag, BadgeProps['variant']> = {
  OK: 'success',
  ORPHAN: 'destructive',
  GHOST_BANK: 'destructive',
  DANGLING: 'destructive',
  STATUS_MISMATCH: 'warning',
  NO_BANK: 'warning',
};

const columns: ColumnDef<BankhubReconcileRow, unknown>[] = [
  {
    id: 'company',
    header: 'Company (BankHub)',
    cell: ({ row }) => (
      <div>
        <div className="font-medium text-surface-900">{row.original.companyName}</div>
        <div className="text-xs text-surface-500">
          {row.original.companyXid} · {row.original.bankhubStatus}
        </div>
      </div>
    ),
  },
  {
    id: 'banks',
    header: 'Ngân hàng',
    cell: ({ row }) => {
      if (row.original.banks.length === 0) return <span className="text-surface-400">—</span>;
      return (
        <div className="space-y-0.5">
          {row.original.banks.map((bank) => (
            <div key={bank.xid} className="flex items-center gap-2 text-surface-700">
              <span>{bank.bankName} · {bank.accountNumber}</span>
              {/* THI-347: the account the org prints on its VietQR. */}
              {row.original.org?.bankAccountXid === bank.xid && (
                <Badge variant="outline" className="border-brand-200 bg-brand-50 text-[11px] text-brand-700">Nhận học phí</Badge>
              )}
            </div>
          ))}
        </div>
      );
    },
  },
  {
    id: 'org',
    header: 'Tổ chức khớp',
    cell: ({ row }) => {
      const org = row.original.org;
      if (!org) return <span className="text-surface-400">—</span>;
      return (
        <Link to={`/orgs/${org.id}`} className="font-medium text-surface-900 hover:underline">
          {org.name}
        </Link>
      );
    },
  },
  {
    id: 'txThisMonth',
    header: 'GD tháng này',
    meta: { headerClassName: 'text-right', cellClassName: 'text-right tabular-nums' } satisfies DataTableColumnMeta,
    cell: ({ row }) => row.original.txThisMonth ?? '—',
  },
  {
    id: 'flag',
    header: 'Trạng thái đối chiếu',
    cell: ({ row }) => (
      <Badge variant={FLAG_VARIANT[row.original.flag]}>{FLAG_LABEL[row.original.flag]}</Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    meta: { headerClassName: 'w-px', cellClassName: 'text-right' } satisfies DataTableColumnMeta,
    cell: ({ row }) => <BankhubRowActions row={row.original} />,
  },
];

function ReconcileTab() {
  const { data, isLoading, error } = useReconcile();
  const [flagFilter, setFlagFilter] = useState<DriftFlag | typeof ALL_FLAGS>(ALL_FLAGS);

  const filtered = useMemo(() => {
    const rows = data?.rows ?? [];
    return flagFilter === ALL_FLAGS ? rows : rows.filter((r) => r.flag === flagFilter);
  }, [data, flagFilter]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={flagFilter} onValueChange={(v) => setFlagFilter(v as DriftFlag | typeof ALL_FLAGS)}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Tất cả trạng thái" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FLAGS}>Tất cả trạng thái</SelectItem>
            {(Object.keys(FLAG_LABEL) as DriftFlag[]).map((flag) => (
              <SelectItem key={flag} value={flag}>
                {FLAG_LABEL[flag]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        total={filtered.length}
        page={1}
        limit={Math.max(filtered.length, 1)}
        sort=""
        onPageChange={() => {}}
        onSortChange={() => {}}
        isLoading={isLoading}
        error={error}
        emptyText={
          flagFilter === ALL_FLAGS ? 'Chưa có company nào.' : 'Không có company nào ở trạng thái này.'
        }
      />
    </div>
  );
}

export function BankHubPage() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">BankHub</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Đối chiếu company/bank BankHub với dữ liệu tổ chức.
        </p>
      </div>

      <ReconcileTab />
    </div>
  );
}
